<script setup>
import { computed, reactive } from 'vue';
import Message from './Message.vue';
import WhatsappAlbumGrid from './bubbles/WhatsappAlbumGrid.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';
import { messageTimestamp } from 'shared/helpers/timeHelper';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

const allMessages = computed(() => {
  return useCamelCase(props.messages, {
    deep: true,
    stopPaths: [
      'content_attributes.translations',
      'content_attributes.whatsapp_flow_response.response_json',
      'content_attributes.reactions',
      'content_attributes.reaction',
    ],
  });
});

const currentChat = useMapGetter('getSelectedChat');

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

const isImageOnlyMessage = msg => {
  if (!msg) return false;
  const atts = msg.attachments || [];
  if (atts.length !== 1) return false;
  const att = atts[0];
  const isImage = att.fileType === 'image' || att.file_type === 'image' || (att.contentType && att.contentType.startsWith('image/'));
  const isSticker =
    msg.contentType === 'sticker' ||
    msg.contentAttributes?.is_sticker === true ||
    msg.contentAttributes?.isSticker === true ||
    (att.dataUrl && att.dataUrl.toLowerCase().includes('.webp')) ||
    (att.extension && att.extension.toLowerCase() === 'webp');

  const hasNoText = !msg.content || msg.content.trim() === '';
  const notPrivate = !msg.private;
  return isImage && !isSticker && hasNoText && notPrivate;
};

// Agrupa mensagens consecutivas de imagem em formato de álbum (estilo WhatsApp)
const displayEntries = computed(() => {
  const result = [];
  const list = allMessages.value || [];
  let i = 0;

  while (i < list.length) {
    const current = list[i];

    if (!isImageOnlyMessage(current)) {
      result.push({ isAlbum: false, message: current, index: i });
      i++;
      continue;
    }

    const currentAtt = current.attachments[0];
    const albumItems = [{
      id: current.id,
      attachmentId: currentAtt.id,
      url: currentAtt.dataUrl || currentAtt.data_url || currentAtt.fileUrl || currentAtt.file_url,
      message: current,
    }];

    let j = i + 1;
    while (j < list.length) {
      const next = list[j];
      if (
        isImageOnlyMessage(next) &&
        next.messageType === current.messageType &&
        (next.senderId === current.senderId || next.sender?.id === current.sender?.id)
      ) {
        const diffSeconds = Math.abs(next.createdAt - current.createdAt);
        if (diffSeconds <= 180) {
          const nextAtt = next.attachments[0];
          albumItems.push({
            id: next.id,
            attachmentId: nextAtt.id,
            url: nextAtt.dataUrl || nextAtt.data_url || nextAtt.fileUrl || nextAtt.file_url,
            message: next,
          });
          j++;
          continue;
        }
      }
      break;
    }

    if (albumItems.length >= 2) {
      const lastMsg = albumItems[albumItems.length - 1].message;
      result.push({
        isAlbum: true,
        id: `album_${current.id}_${lastMsg.id}`,
        messageType: current.messageType,
        items: albumItems,
        primaryMessage: lastMsg,
      });
      i = j;
    } else {
      result.push({ isAlbum: false, message: current, index: i });
      i++;
    }
  }

  return result;
});

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // Then check fetch cache
  if (!replyMessage && fetchedReplyMessages.has(inReplyToMessageId)) {
    replyMessage = fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (!replyMessage && currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return replyMessage ? useCamelCase(replyMessage) : null;
};
</script>

<template>
  <ul class="px-4 bg-n-surface-1">
    <slot name="beforeAll" />
    <template v-for="entry in displayEntries" :key="entry.isAlbum ? entry.id : entry.message.id">
      <slot
        v-if="firstUnreadId && !entry.isAlbum && entry.message.id === firstUnreadId"
        name="unreadBadge"
      />

      <!-- Renderização de Álbum Estilo WhatsApp -->
      <li
        v-if="entry.isAlbum"
        class="flex w-full group/msg my-2"
        :class="entry.messageType === MESSAGE_TYPES.INCOMING ? 'justify-start' : 'justify-end'"
      >
        <div
          class="flex items-center gap-1.5 max-w-[85%] relative"
          :class="entry.messageType === MESSAGE_TYPES.INCOMING ? 'flex-row' : 'flex-row-reverse'"
        >
          <WhatsappAlbumGrid
            :items="entry.items"
            :time="messageTimestamp(entry.primaryMessage.createdAt)"
            :status="entry.primaryMessage.status"
            :is-outgoing="entry.messageType === MESSAGE_TYPES.OUTGOING"
          />
        </div>
      </li>

      <!-- Renderização Padrão -->
      <Message
        v-else
        v-bind="entry.message"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="getInReplyToMessage(entry.message)"
        :group-with-next="shouldGroupWithNext(entry.index, allMessages)"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        data-clarity-mask="True"
        @retry="emit('retry', entry.message)"
      />
    </template>
    <slot name="after" />
  </ul>
</template>
